import { useMutation, useQuery } from '@tanstack/react-query';
import { useSelector } from 'react-redux';

import type {
  ZuubOnboardingBeginRequest,
  ZuubOnboardingBeginResponse,
  ZuubOnboardingStatusResponse,
  ZuubPayer,
} from '@/@types/zuub/contracts';

import { makeAPIRequestReactQueryWithHeaders } from '../api';
import { endpoints } from '../endpoints';

// Hook to get list of payers that support portal connections
export const useZuubRcmOnboardingPayers = () => {
  const {
    primaryPracticeId: currentPractice,
    primaryOrganizationId: currentOrg,
  } = useSelector(
    (state: {
      login: {
        user: {
          primaryPracticeId?: string;
          primaryOrganizationId?: string;
        };
      };
    }) => state.login.user,
  );

  return useQuery<ZuubPayer[]>(
    [endpoints.rcmZuubOnboarding.payers],
    () =>
      makeAPIRequestReactQueryWithHeaders<ZuubPayer[]>(
        endpoints.rcmZuubOnboarding.payers,
        undefined,
        {
          Org: currentOrg,
          Practice: currentPractice,
        },
        'GET',
      ),
    {
      enabled: Boolean(currentPractice && currentOrg),
      staleTime: 5 * 60 * 1000, // 5 minutes
    },
  );
};

// Hook to begin onboarding process
export const useZuubRcmOnboardingBegin = () => {
  const {
    primaryPracticeId: currentPractice,
    primaryOrganizationId: currentOrg,
  } = useSelector(
    (state: {
      login: {
        user: {
          primaryPracticeId?: string;
          primaryOrganizationId?: string;
        };
      };
    }) => state.login.user,
  );

  return useMutation<
    ZuubOnboardingBeginResponse,
    Error,
    ZuubOnboardingBeginRequest
  >((data) =>
    makeAPIRequestReactQueryWithHeaders<ZuubOnboardingBeginResponse>(
      endpoints.rcmZuubOnboarding.begin,
      undefined,
      {
        Org: currentOrg,
        Practice: currentPractice,
      },
      'POST',
      {
        data,
      },
    ),
  );
};

// Hook to check onboarding status with polling
export const useZuubRcmOnboardingStatus = (
  payerId: number,
  enabled: boolean = true,
) => {
  const {
    primaryPracticeId: currentPractice,
    primaryOrganizationId: currentOrg,
  } = useSelector(
    (state: {
      login: {
        user: {
          primaryPracticeId?: string;
          primaryOrganizationId?: string;
        };
      };
    }) => state.login.user,
  );

  return useQuery<ZuubOnboardingStatusResponse>(
    [endpoints.rcmZuubOnboarding.status, payerId],
    () =>
      makeAPIRequestReactQueryWithHeaders<ZuubOnboardingStatusResponse>(
        `${endpoints.rcmZuubOnboarding.status}/${payerId}`,
        undefined,
        {
          Org: currentOrg,
          Practice: currentPractice,
        },
        'GET',
      ),
    {
      enabled: Boolean(currentPractice && currentOrg && enabled),
      refetchInterval: (data) => {
        // Stop polling if status is Succeeded or Failed
        if (
          data?.credentialStatus === 'Succeeded' ||
          data?.credentialStatus === 'Failed'
        ) {
          return false;
        }
        // Poll every 3 seconds for other statuses
        return 3000;
      },
      staleTime: 0, // Always consider data stale to enable polling
    },
  );
};
